import {type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {SquarePen} from 'lucide-react';
import {Button, InfoBox, InputOTP, InputOTPGroup, InputOTPSlot} from '@chekinapp/ui';
import {CODE_LENGTH} from './constants';

type Props = {
  code: string;
  onCodeChange: (code: string) => void;
  errorMessage?: string;
  isCodeMismatch?: boolean;
  note: ReactNode;
  noteIcon: ReactNode;
  changeLabel: string;
  onResend: () => Promise<void>;
  onChangeContacts: () => void;
  isLoading?: boolean;
};

const ContactsVerificationCodes = ({
  code,
  onCodeChange,
  errorMessage,
  isCodeMismatch = false,
  note,
  noteIcon,
  changeLabel,
  onResend,
  onChangeContacts,
  isLoading = false,
}: Props) => {
  const {t} = useTranslation();
  const hasError = Boolean(errorMessage);

  return (
    <div className="flex flex-col gap-4">
      {!hasError && <InfoBox icon={noteIcon}>{note}</InfoBox>}

      <div className="flex flex-col items-center gap-4 rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] px-5 py-[26px] [box-shadow:var(--card-shadow)]">
        <InputOTP
          value={code}
          onChange={onCodeChange}
          maxLength={CODE_LENGTH}
          error={isCodeMismatch}
          disabled={isLoading}
          autoFocus
        >
          <InputOTPGroup>
            {Array.from({length: CODE_LENGTH}).map((_, index) => (
              <InputOTPSlot key={index} index={index} />
            ))}
          </InputOTPGroup>
        </InputOTP>

        {hasError && (
          <p className="m-0 text-center text-[12.5px] font-semibold text-[var(--chekin-red-soft)]">
            {errorMessage}
          </p>
        )}

        <p className="m-0 text-[12px] text-[var(--chekin-text-tertiary)]">
          {t('didnt_get_the_code')}{' '}
          <Button
            variant="link"
            className="h-auto p-0 text-[12px] font-semibold"
            onClick={() => void onResend()}
            disabled={isLoading}
          >
            {t('resend_code')}
          </Button>
        </p>
      </div>

      <div className="flex flex-col items-center">
        <Button variant="link" onClick={onChangeContacts} disabled={isLoading}>
          <SquarePen className="h-[18px] w-[18px]" />
          {changeLabel}
        </Button>
      </div>
    </div>
  );
};

export {ContactsVerificationCodes};
